/**
 * MyEvaluationSites — operator-wide listing of every site they've created
 * across all of their evaluations. Reached from MAIN/Evaluations →
 * Evaluation Sites, alongside Approvals.
 *
 * Sites are top-level entities now (many-to-many with evaluations), so a
 * site appears here whether it's linked to one, many, or zero evaluations.
 * From here the operator can:
 *   - Browse as a list or on a map, mirroring the Locations index
 *   - Edit site details (owner-scoped) in the right-side sheet
 *   - Re-attach a site to any of their evaluations
 *   - Permanently delete a site (cascades scores)
 *
 * Selecting a site in EITHER view opens the same SiteEditSheet, so the
 * address can be corrected without leaving the page — which is the main
 * reason to come here for a site the geocoder could not place.
 *
 * Loosely mirrors the visual conventions of the Locations index: shell,
 * heading, search + filter pills, List/Map toggle.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import Spinner from 'components/Spinner'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from 'components/ui/dropdown-menu'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import {
	Building2,
	Search,
	MoreVertical,
	Plus,
	Trash2,
	Pencil,
	ClipboardCheck,
	List,
	Map as MapIcon,
	AlertCircle,
} from 'lucide-react'
import { cn } from 'lib/utils'
import SiteEditSheet from './components/SiteEditSheet'
import LocationsIndexMap from 'screens/locations/components/Locations/LocationsIndexMap'
import type { LocationIndexMarker } from 'screens/locations/components/Locations/LocationsIndexMap'
import { toMappableSites, toPreciseSites } from './utils/siteMapPosition'
import { fetchAllSites, linkSiteToEvaluation, hardDeleteSite } from 'slices/evaluationSiteSlice'
import { initializeEvaluations } from 'slices/evaluationSlice'
import routes from 'constants/routes'

type LinkFilter = 'ALL' | 'LINKED' | 'UNLINKED'
type ViewMode = 'list' | 'map'

// Persisted so the operator's preferred view survives navigation, matching
// the Locations index's `locationsViewMode`.
const VIEW_MODE_KEY = 'evaluationSitesViewMode'

const MyEvaluationSites = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const [linkFilter, setLinkFilter] = useState<LinkFilter>('ALL')
	const [viewMode, setViewMode] = useState<ViewMode>(() => {
		try {
			return localStorage.getItem(VIEW_MODE_KEY) === 'map' ? 'map' : 'list'
		} catch {
			// Private browsing / storage disabled — fall back to the default.
			return 'list'
		}
	})
	const mapRef = useRef<any>(null)

	const handleViewModeChange = useCallback((mode: ViewMode) => {
		setViewMode(mode)
		try {
			localStorage.setItem(VIEW_MODE_KEY, mode)
		} catch {
			// Non-fatal: the view just won't persist.
		}
	}, [])

	const [editSheetSite, setEditSheetSite] = useState<any>(null)
	const [linkPickerSite, setLinkPickerSite] = useState<any>(null)
	const [deleteSite, setDeleteSite] = useState<any>(null)
	const [deleting, setDeleting] = useState(false)
	// Why the delete did not happen, kept beside the button that failed.
	const [deleteError, setDeleteError] = useState<string | null>(null)

	const { entities, allSitesInitialized, isLoading } = useSelector(
		(state: any) => state.evaluationSites,
	)

	const evaluations = useSelector((state: any) => {
		const evalState = state.evaluations
		if (!evalState) return []
		return (evalState.ids || []).map((id: string) => evalState.entities[id]).filter(Boolean)
	})

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchAllSites())
		// @ts-ignore — thunk
		dispatch(initializeEvaluations())
	}, [dispatch])

	const allSites = useMemo(() => Object.values(entities) as any[], [entities])

	const filtered = useMemo(() => {
		let result = allSites
		if (linkFilter === 'LINKED') {
			result = result.filter((s) => (s.evaluations || []).length > 0)
		} else if (linkFilter === 'UNLINKED') {
			result = result.filter((s) => (s.evaluations || []).length === 0)
		}
		if (searchValue.trim()) {
			const q = searchValue.toLowerCase()
			result = result.filter((s) => {
				return (
					(s.name || '').toLowerCase().includes(q) ||
					(s.address || '').toLowerCase().includes(q) ||
					(s.city || '').toLowerCase().includes(q) ||
					(s.state || '').toLowerCase().includes(q) ||
					(s.postalCode || '').toLowerCase().includes(q) ||
					(s.propertyType || '').toLowerCase().includes(q)
				)
			})
		}
		return result.sort((a, b) => {
			const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime()
			const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime()
			return bTime - aTime
		})
	}, [allSites, linkFilter, searchValue])

	// Map markers, built from the SAME filtered list the table shows, so the
	// search box and filter pills apply to both views. Sites whose address
	// never geocoded fall back to their city/postal centre via
	// toMappableSites rather than disappearing from the map.
	const mapMarkers = useMemo<LocationIndexMarker[]>(
		() =>
			toMappableSites(filtered).map((site) => ({
				id: String(site._id),
				lat: site.latitude,
				lng: site.longitude,
				name: site.name,
				image: site.location?.image || site.coverImage || site.imageUrls?.[0],
				address: site.address,
				city: site.city,
				stateProvince: site.state,
				category: 'active',
			})),
		[filtered],
	)

	const approximateCount = useMemo(
		() => toMappableSites(filtered).length - toPreciseSites(filtered).length,
		[filtered],
	)
	const unplaceableCount = filtered.length - mapMarkers.length

	const handleMapMarkerClick = useCallback(
		(markerId: string) => {
			// Same destination as clicking a table row — one edit surface.
			const site = filtered.find((s: any) => String(s._id) === markerId)
			if (site) setEditSheetSite(site)
		},
		[filtered],
	)

	const handleLink = useCallback(
		async (siteId: string, evalId: string) => {
			// @ts-ignore — thunk
			await dispatch(linkSiteToEvaluation({ siteId, evalId }))
			setLinkPickerSite(null)
		},
		[dispatch],
	)

	const handleHardDelete = useCallback(async () => {
		if (!deleteSite) return
		setDeleting(true)
		setDeleteError(null)
		try {
			// @ts-ignore — thunk
			const action = await dispatch(hardDeleteSite(deleteSite._id))

			// A rejected thunk still RESOLVES, so this has to be checked. Closing
			// the dialog unconditionally reported a refused delete as a success.
			if ((action as any)?.meta?.requestStatus === 'rejected') {
				setDeleteError(String((action as any).payload ?? 'The site could not be deleted.'))
				return
			}
			setDeleteSite(null)
		} finally {
			setDeleting(false)
		}
	}, [dispatch, deleteSite])

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<div className="mb-2">
							<h2 className="text-2xl font-semibold text-gray-800">Evaluation Sites</h2>
							<p className="text-sm text-gray-500 mt-0.5">
								Every site you've created across all of your evaluations. Edit details,
								attach to evaluations, or remove permanently. Distinct from the
								per-evaluation Sites tab.
							</p>
						</div>

						{/* Toolbar */}
						<div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4 mb-4">
							<div className="relative w-full sm:max-w-sm">
								<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									value={searchValue}
									onChange={(e) => setSearchValue(e.target.value)}
									placeholder="Search sites..."
									className="pl-8"
								/>
							</div>
							<div className="flex items-center gap-1.5 flex-wrap">
								{(['ALL', 'LINKED', 'UNLINKED'] as LinkFilter[]).map((f) => (
									<button
										key={f}
										type="button"
										onClick={() => setLinkFilter(f)}
										className={cn(
											'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
											linkFilter === f
												? 'bg-[#0080fe] text-white'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
										)}
									>
										{f === 'ALL'
											? 'All'
											: f === 'LINKED'
												? 'In an evaluation'
												: 'Not in any evaluation'}
									</button>
								))}
							</div>
							<div className="ml-auto flex items-center gap-3">
								<span className="text-xs text-gray-500">
									{filtered.length} {filtered.length === 1 ? 'site' : 'sites'}
								</span>
								{/* List / Map toggle — same control as the Locations index. */}
								<div className="flex items-center rounded-lg bg-gray-100 p-0.5">
									{(['list', 'map'] as ViewMode[]).map((mode) => (
										<button
											key={mode}
											type="button"
											onClick={() => handleViewModeChange(mode)}
											className={cn(
												'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
												viewMode === mode
													? 'bg-white text-[#0080fe] shadow-sm'
													: 'text-gray-500 hover:text-gray-700',
											)}
										>
											{mode === 'list' ? (
												<List className="h-3.5 w-3.5" />
											) : (
												<MapIcon className="h-3.5 w-3.5" />
											)}
											{mode === 'list' ? 'List' : 'Map'}
										</button>
									))}
								</div>
							</div>
						</div>

						{/* Placement note — only meaningful on the map. */}
						{viewMode === 'map' && (approximateCount > 0 || unplaceableCount > 0) && (
							<div className="mb-3 flex flex-wrap gap-2">
								{approximateCount > 0 && (
									<div className="inline-flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
										<AlertCircle className="h-3 w-3" />
										{approximateCount} site{approximateCount === 1 ? '' : 's'} shown at the
										centre of {approximateCount === 1 ? 'its' : 'their'} city or postal code —
										open {approximateCount === 1 ? 'it' : 'them'} to add a street address
									</div>
								)}
								{unplaceableCount > 0 && (
									<div className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-600">
										<AlertCircle className="h-3 w-3" />
										{unplaceableCount} site{unplaceableCount === 1 ? '' : 's'} not on the map —
										no address or city yet
									</div>
								)}
							</div>
						)}

						{/* Content */}
						{isLoading && !allSitesInitialized ? (
							<Spinner containerHeight="50vh" />
						) : filtered.length === 0 ? (
							<div className="text-center py-20">
								<Building2 className="mx-auto h-12 w-12 text-gray-300 mb-3" />
								<p className="text-sm text-gray-500">
									{allSites.length === 0
										? "You haven't created any sites yet. Open an evaluation and add a site to begin."
										: 'No sites match your filters.'}
								</p>
							</div>
						) : viewMode === 'map' ? (
							mapMarkers.length > 0 ? (
								<div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
									<LocationsIndexMap
										ref={mapRef}
										markers={mapMarkers}
										onMarkerClick={handleMapMarkerClick}
									/>
								</div>
							) : (
								<div className="text-center py-20">
									<Building2 className="mx-auto h-12 w-12 text-gray-300 mb-3" />
									<p className="text-sm text-gray-500">
										None of these sites can be placed on a map yet. Open one and add at least
										a city or postal code.
									</p>
								</div>
							)
						) : (
							<div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Name</TableHead>
											<TableHead>Address</TableHead>
											<TableHead className="w-32">Type</TableHead>
											<TableHead className="w-64">Evaluations</TableHead>
											<TableHead className="w-12" aria-label="Actions" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{filtered.map((site: any) => {
											const evals = site.evaluations || []
											const addressLine = [site.address, site.city, site.state, site.postalCode]
												.filter(Boolean)
												.join(', ')
											return (
												<TableRow
													key={site._id}
													className="cursor-pointer"
													onClick={() => setEditSheetSite(site)}
												>
													<TableCell className="font-medium text-gray-900">
														<Link
															to={`${routes.ALL_SITES}/${site._id}`}
															onClick={(e) => e.stopPropagation()}
															className="text-gray-900 no-underline hover:text-[#0080fe] hover:underline"
														>
															{site.name}
														</Link>
													</TableCell>
													<TableCell className="text-gray-600 text-sm">
														{addressLine || <span className="text-gray-300">—</span>}
													</TableCell>
													<TableCell className="text-gray-500 text-xs">
														{site.propertyType || <span className="text-gray-300">—</span>}
													</TableCell>
													<TableCell>
														{evals.length === 0 ? (
															<span className="text-xs text-gray-400 italic">Unlinked</span>
														) : (
															<div className="flex flex-wrap gap-1">
																{evals.map((evalRef: any) => {
																	const evalId =
																		typeof evalRef === 'string'
																			? evalRef
																			: String(evalRef._id ?? evalRef)
																	const evalName =
																		typeof evalRef === 'object' && evalRef?.name
																			? evalRef.name
																			: evalId.slice(-6)
																	return (
																		<Link
																			key={evalId}
																			to={`${routes.EVALUATIONS}/${evalId}/sites`}
																			onClick={(e) => e.stopPropagation()}
																			className="no-underline"
																		>
																			<Badge
																				variant="secondary"
																				className="text-[10px] gap-0.5 hover:bg-gray-200"
																			>
																				<ClipboardCheck className="w-2.5 h-2.5" />
																				{evalName}
																			</Badge>
																		</Link>
																	)
																})}
															</div>
														)}
													</TableCell>
													<TableCell className="text-right">
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<button
																	type="button"
																	onClick={(e) => e.stopPropagation()}
																	className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
																	aria-label="Site actions"
																>
																	<MoreVertical className="w-4 h-4" />
																</button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																<DropdownMenuItem
																	onClick={(e) => {
																		e.stopPropagation()
																		setEditSheetSite(site)
																	}}
																>
																	<Pencil className="w-3.5 h-3.5 mr-2" />
																	Edit
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={(e) => {
																		e.stopPropagation()
																		setLinkPickerSite(site)
																	}}
																>
																	<Plus className="w-3.5 h-3.5 mr-2" />
																	Add to evaluation
																</DropdownMenuItem>
																<DropdownMenuItem
																	onClick={(e) => {
																		e.stopPropagation()
																		setDeleteSite(site)
																	}}
																	className="text-red-600"
																>
																	<Trash2 className="w-3.5 h-3.5 mr-2" />
																	Delete permanently
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							</div>
						)}
					</main>
				</div>
			</div>

			{/* Edit sheet — operator-scoped (no evalId) so destructive button is
			    "Delete Permanently" and dispatches hardDeleteSite. */}
			<SiteEditSheet
				open={!!editSheetSite}
				onClose={() => setEditSheetSite(null)}
				site={editSheetSite}
			/>

			{/* Add to evaluation picker */}
			<Dialog open={!!linkPickerSite} onClose={() => setLinkPickerSite(null)}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<ClipboardCheck className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>Add to evaluation</DialogTitle>
								<DialogDescription>
									Pick an evaluation to attach "{linkPickerSite?.name}" to.
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						{evaluations.length === 0 ? (
							<p className="text-sm text-gray-500 text-center py-6">
								You don't have any evaluations yet.
							</p>
						) : (
							<ul className="max-h-80 overflow-y-auto space-y-1">
								{evaluations.map((ev: any) => {
									const linked = (linkPickerSite?.evaluations || []).some((ref: any) => {
										const id = typeof ref === 'string' ? ref : String(ref._id ?? ref)
										return id === String(ev._id)
									})
									return (
										<li key={ev._id}>
											<button
												type="button"
												disabled={linked}
												onClick={() => handleLink(linkPickerSite._id, ev._id)}
												className={cn(
													'w-full text-left px-3 py-2 rounded-md border text-sm transition-colors',
													linked
														? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
														: 'border-gray-200 hover:bg-[#0080fe]/5 hover:border-[#0080fe]/30 text-gray-800',
												)}
											>
												<div className="font-medium">{ev.name}</div>
												{ev.marketArea && (
													<div className="text-xs text-gray-500">{ev.marketArea}</div>
												)}
												{linked && (
													<div className="text-[10px] text-gray-400 italic mt-0.5">
														Already attached
													</div>
												)}
											</button>
										</li>
									)
								})}
							</ul>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Permanent delete confirmation */}
			<Dialog
				open={!!deleteSite}
				onClose={() => {
					setDeleteSite(null)
					setDeleteError(null)
				}}
			>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Permanently delete this site?</DialogTitle>
						<DialogDescription>
							"{deleteSite?.name}" will be permanently deleted along with every score
							recorded for it across every evaluation. This cannot be undone.
						</DialogDescription>
					</DialogHeader>
					{/* Padded body. DialogContent carries no padding of its own, so
					    without this wrapper the error and the buttons sit flush
					    against the bottom-right corner of the dialog. */}
					<div className="px-5 py-4">
						{deleteError && (
							<p role="alert" className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
								{deleteError}
							</p>
						)}
						<div className="flex justify-end gap-2">
							<Button variant="ghost" onClick={() => setDeleteSite(null)} disabled={deleting}>
								Cancel
							</Button>
							<Button variant="danger" onClick={handleHardDelete} disabled={deleting}>
								{deleting ? 'Deleting...' : 'Delete Permanently'}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default MyEvaluationSites
